/**
 * Catering on a venue — one source of truth for the amenity chips that
 * describe it, shared by the listing form (constants.ts), the venue FAQ
 * (utils/venueSchema.mjs) and demand → venue matching (DemandForm).
 *
 * Catering is stored as amenity chips rather than a separate field so it
 * rides every path amenities already travel: the search filter, the API's
 * `amenities` query, JSON-LD amenityFeature and the Markdown twin.
 *
 * The chips only ever state what a host ticked. A venue with neither chip
 * has *unknown* catering, not "no catering" — never render it as a refusal.
 */

/** The venue provides food itself (own kitchen, restaurant or catering team). */
export const CATERING_IN_HOUSE = 'Vlastný catering';
/** The organiser may bring an outside caterer. */
export const CATERING_EXTERNAL_ALLOWED = 'Externý catering povolený';

/** Chips that mean "food can be arranged here" — either way. */
export const CATERING_AMENITIES = [CATERING_IN_HOUSE, CATERING_EXTERNAL_ALLOWED];

/** Service formats and offerings */
export const CATERING_SERVICE_AMENITIES = [
  'Coffee break',
  'Raut / Švédske stoly',
  'Servírované menu',
  'Raňajky / Brunch',
  'Teplý raut',
  'Studený raut',
  'Dezerty / Candy bar',
];

/** Dietary requirements & special food options */
export const CATERING_DIET_AMENITIES = [
  'Vegetariánske menu',
  'Vegánske menu',
  'Bezlepkové menu',
  'Bezlaktózové menu',
];

/** Beverage & bar service options */
export const CATERING_BEVERAGE_AMENITIES = [
  'Bar s obsluhou',
  'Vlastný alkohol povolený',
  'Nápojové balíky',
  'Korkovné / poplatok za vlastné nápoje',
  'Degustácia vína / piva',
];

/** Service formats and policies that belong next to catering in the form. */
export const CATERING_RELATED_AMENITIES = [
  ...CATERING_SERVICE_AMENITIES,
  ...CATERING_DIET_AMENITIES,
  ...CATERING_BEVERAGE_AMENITIES,
];

/**
 * "Zázemie pre catering" — what a caterer finds on site. Answers the three
 * questions a caterer asks before quoting: can I cook here, can I plug in and
 * wash up, can I unload. Stored as amenity chips like the rest.
 */

/** Kitchen level — mutually exclusive, pick at most one. */
export const CATERING_KITCHEN_NONE = 'Bez zázemia pre catering (drop-off / suchý raut)';
export const CATERING_KITCHEN_PREP = 'Prípravovňa / backstage pre catering';
export const CATERING_KITCHEN_FULL = 'Plne vybavená gastro kuchyňa';
export const CATERING_KITCHEN_LEVELS = [CATERING_KITCHEN_NONE, CATERING_KITCHEN_PREP, CATERING_KITCHEN_FULL];

/** Technical capacities. */
export const CATERING_POWER_380V = '380V (silový prúd)';
export const CATERING_WATER = 'Teplá/studená voda a odpad';
export const CATERING_TECHNICAL = [CATERING_POWER_380V, CATERING_WATER];

/** Logistics. */
export const CATERING_UNLOADING = 'Vykládka pri vchode / nákladný výťah';
export const CATERING_CREW_PARKING = 'Parkovanie pre štáb';
export const CATERING_LOGISTICS = [CATERING_UNLOADING, CATERING_CREW_PARKING];

/** The questionnaire's sections, in form order. */
export const CATERING_FACILITY_GROUPS = [
  { id: 'kitchen', title: 'Úroveň zázemia', options: CATERING_KITCHEN_LEVELS, exclusive: true },
  { id: 'technical', title: 'Technické kapacity', options: CATERING_TECHNICAL, exclusive: false },
  { id: 'logistics', title: 'Logistika', options: CATERING_LOGISTICS, exclusive: false },
];

/** Every facility chip, in form order. */
export const CATERING_FACILITY_AMENITIES = CATERING_FACILITY_GROUPS.flatMap((g) => g.options);

/** Every catering-related chip, in form order. */
export const ALL_CATERING_AMENITIES = [
  ...CATERING_AMENITIES,
  ...CATERING_SERVICE_AMENITIES,
  ...CATERING_DIET_AMENITIES,
  ...CATERING_BEVERAGE_AMENITIES,
  ...CATERING_FACILITY_AMENITIES,
];

/**
 * Toggle one chip in an amenity list. Choosing a kitchen level drops any other
 * level (they contradict each other); choosing it again clears it, so "not
 * answered" stays reachable.
 *
 * @param {string[]} amenities
 * @param {string} chip
 * @returns {string[]}
 */
export function toggleCateringChip(amenities, chip) {
  const list = Array.isArray(amenities) ? amenities : [];
  if (list.includes(chip)) return list.filter((a) => a !== chip);
  const rest = CATERING_KITCHEN_LEVELS.includes(chip)
    ? list.filter((a) => !CATERING_KITCHEN_LEVELS.includes(a))
    : list;
  return [...rest, chip];
}

/**
 * @param {unknown} amenities a listing's `spaceDetails.amenities`
 * @returns {{ inHouse: boolean, externalAllowed: boolean, any: boolean }}
 */
export function cateringOptions(amenities) {
  const list = Array.isArray(amenities) ? amenities : [];
  const inHouse = list.includes(CATERING_IN_HOUSE);
  const externalAllowed = list.includes(CATERING_EXTERNAL_ALLOWED);
  return { inHouse, externalAllowed, any: inHouse || externalAllowed };
}

/**
 * The FAQ answer for "Je v priestore možný catering?", or null when the host
 * has not said — an unanswered question is left out, never guessed.
 *
 * @param {unknown} amenities
 * @param {string} [name] venue name
 * @returns {string | null}
 */
export function cateringFaqAnswer(amenities, name = 'Tento priestor') {
  const { inHouse, externalAllowed } = cateringOptions(amenities);
  if (inHouse && externalAllowed) {
    return `Áno. ${name} ponúka vlastný catering a povoľuje aj externý.`;
  }
  if (inHouse) return `Áno, ${name} ponúka vlastný catering.`;
  if (externalAllowed) return `Áno, ${name} povoľuje externý catering — občerstvenie si zabezpečíte u vlastného dodávateľa.`;
  return null;
}

/**
 * The FAQ answer for "Aké zázemie má priestor pre catering?", or null when the
 * host ticked none of the facility chips. Only restates what was ticked.
 *
 * @param {unknown} amenities
 * @returns {string | null}
 */
export function cateringFacilitiesFaqAnswer(amenities) {
  const list = Array.isArray(amenities) ? amenities : [];
  const parts = [];
  if (list.includes(CATERING_KITCHEN_FULL)) {
    parts.push('Priestor má plne vybavenú gastro kuchyňu (konvektomat, varná zóna).');
  } else if (list.includes(CATERING_KITCHEN_PREP)) {
    parts.push('Priestor má prípravovňu / backstage pre catering (chladenie, výdaj, bez varenia).');
  } else if (list.includes(CATERING_KITCHEN_NONE)) {
    parts.push('Priestor nemá zázemie pre catering — vhodný je drop-off alebo suchý raut.');
  }
  const tech = [];
  if (list.includes(CATERING_POWER_380V)) tech.push('silový prúd 380V');
  if (list.includes(CATERING_WATER)) tech.push('prístup k teplej a studenej vode a odpadu');
  if (tech.length) parts.push(`K dispozícii je ${tech.join(' a ')}.`);
  const logistics = [];
  if (list.includes(CATERING_UNLOADING)) logistics.push('vykládka autom priamo pri vchode alebo nákladný výťah');
  if (list.includes(CATERING_CREW_PARKING)) logistics.push('parkovanie pre štáb');
  if (logistics.length) parts.push(`Logistika: ${logistics.join(', ')}.`);
  return parts.length ? parts.join(' ') : null;
}

/**
 * Whether a demand asked for catering. `extras` arrives from D1 as a JSON
 * string (`'["catering"]'`), from the form as an array.
 *
 * @param {unknown} extras
 * @returns {boolean}
 */
export function demandWantsCatering(extras) {
  let list = extras;
  if (typeof list === 'string') {
    try { list = JSON.parse(list); } catch { return false; }
  }
  return Array.isArray(list) && list.includes('catering');
}

/**
 * FAQ answer for special diets available in the venue.
 *
 * @param {unknown} amenities
 * @returns {string | null}
 */
export function cateringDietFaqAnswer(amenities) {
  const list = Array.isArray(amenities) ? amenities : [];
  const diets = list.filter((a) => CATERING_DIET_AMENITIES.includes(a));
  if (diets.length === 0) return null;
  return `Áno, priestor vie po dohode zabezpečiť: ${diets.join(', ')}.`;
}

/**
 * FAQ answer for bar and beverage options.
 *
 * @param {unknown} amenities
 * @returns {string | null}
 */
export function cateringDrinksFaqAnswer(amenities) {
  const list = Array.isArray(amenities) ? amenities : [];
  const drinks = list.filter((a) => CATERING_BEVERAGE_AMENITIES.includes(a));
  if (drinks.length === 0) return null;
  return `V oblasti nápojov je k dispozícii: ${drinks.join(', ')}.`;
}

/**
 * Categorize a listing's amenities into clean, professional semantic groups
 * for structured display on listing detail, demand matching and host setup.
 *
 * @param {unknown} amenities
 * @returns {{
 *   catering: string[],
 *   beverages: string[],
 *   tech: string[],
 *   spaceComfort: string[],
 *   activitiesOther: string[]
 * }}
 */
export function categorizeVenueAmenities(amenities) {
  const list = Array.isArray(amenities) ? amenities : [];

  const catering = list.filter((a) =>
    CATERING_AMENITIES.includes(a) ||
    CATERING_SERVICE_AMENITIES.includes(a) ||
    CATERING_DIET_AMENITIES.includes(a) ||
    CATERING_FACILITY_AMENITIES.includes(a) ||
    a === 'Reštaurácia' || a === 'Kuchynka' || a === 'Gril'
  );

  const beverages = list.filter((a) =>
    CATERING_BEVERAGE_AMENITIES.includes(a) ||
    a === 'Bar' || a === 'Kávovar' || a === 'Pitný režim (voda/čaj)' || a === 'Chladnička'
  );

  const tech = list.filter((a) => [
    'Wifi', 'Projektor/TV', 'Plátna rôznych rozmerov', 'Obrazovky / LED steny',
    'Audio technika', 'Video technika', 'Mikrofóny', 'Pódium', 'Flipchart',
    'Biela tabuľa', 'Profesionálne svetlá a zvuk', 'Haser (dymostroj)',
    'Tlmočenie', 'Livestream', 'Video spracovanie eventu'
  ].includes(a));

  const spaceComfort = list.filter((a) => [
    'Klimatizácia', 'Denné svetlo', 'Zatemnenie', 'Zvuková izolácia',
    'Bezbariérový prístup', 'Šatňa', 'Make-up kútik', 'Lounge / Oddychová zóna',
    'Terasa / Balkón', 'Záhrada / Exteriér', 'Fajčiarsky kútik', 'Ubytovanie',
    'Parkovanie', 'Recepcia / Vrátnica', 'Bezpečnostný systém / Alarm', 'Úložný priestor'
  ].includes(a));

  const activitiesOther = list.filter((a) =>
    !catering.includes(a) &&
    !beverages.includes(a) &&
    !tech.includes(a) &&
    !spaceComfort.includes(a)
  );

  return {
    catering,
    beverages,
    tech,
    spaceComfort,
    activitiesOther,
  };
}

function normalizeCateringText(s) {
  return (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Checks whether a listing allows own/external catering.
 * Evaluates amenity chips, space details and description keywords.
 * @param {any} listing
 * @returns {boolean}
 */
export function allowsExternalCatering(listing) {
  if (!listing) return false;
  const details = listing.spaceDetails || {};
  const amenities = Array.isArray(details.amenities)
    ? details.amenities
    : Array.isArray(listing.amenities)
    ? listing.amenities
    : [];

  if (
    amenities.includes(CATERING_EXTERNAL_ALLOWED) ||
    amenities.includes('Externý catering povolený') ||
    amenities.includes(CATERING_KITCHEN_PREP) ||
    amenities.includes(CATERING_KITCHEN_FULL)
  ) {
    return true;
  }

  const text = normalizeCateringText(`${listing.title || ''} ${listing.description || ''} ${details.terms || ''}`);
  return (
    text.includes('externy catering') ||
    text.includes('vlastny catering') ||
    text.includes('vlastne obcerstvenie') ||
    text.includes('vlastne jedlo') ||
    text.includes('moznost cateringu') ||
    text.includes('moznost vlastneho cateringu')
  );
}

/**
 * Checks whether a listing allows corkage / own alcohol.
 * Evaluates amenity chips, space details and description keywords.
 * @param {any} listing
 * @returns {boolean}
 */
export function allowsCorkage(listing) {
  if (!listing) return false;
  const details = listing.spaceDetails || {};
  const amenities = Array.isArray(details.amenities)
    ? details.amenities
    : Array.isArray(listing.amenities)
    ? listing.amenities
    : [];

  if (
    amenities.includes('Vlastný alkohol povolený') ||
    amenities.includes('Korkovné / poplatok za vlastné nápoje') ||
    amenities.includes('Korkovné')
  ) {
    return true;
  }

  const text = normalizeCateringText(`${listing.title || ''} ${listing.description || ''} ${details.terms || ''}`);
  return (
    text.includes('korkovn') ||
    text.includes('vlastny alkohol') ||
    text.includes('vlastne vino') ||
    text.includes('vlastne napoje') ||
    text.includes('moznost vlastneho alkoholu') ||
    text.includes('poplatok za vlastne')
  );
}

/**
 * Checks whether a demand requested own / external catering.
 * @param {unknown} extras
 * @returns {boolean}
 */
export function demandWantsOwnCatering(extras) {
  let list = extras;
  if (typeof list === 'string') {
    try { list = JSON.parse(list); } catch { return false; }
  }
  return Array.isArray(list) && (list.includes('own_catering') || list.includes('vlastny_catering'));
}

/**
 * Checks whether a demand requested corkage / own alcohol.
 * @param {unknown} extras
 * @returns {boolean}
 */
export function demandWantsCorkage(extras) {
  let list = extras;
  if (typeof list === 'string') {
    try { list = JSON.parse(list); } catch { return false; }
  }
  return Array.isArray(list) && (list.includes('corkage') || list.includes('korkovne'));
}

